import React, { useCallback, useEffect, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import { toast } from 'sonner';
import { downloadAuthedFile } from '@/lib/fileDownload';
import { useAuth } from '@/contexts/AuthContext';
import { canSeeAggregateMoney } from '@/lib/constants';
import { useIsMobile } from '@/hooks/useIsMobile';
import { ChipRow } from '@/components/phone/primitives';
import { BarChart3, MapPin, Clock, TruckIcon, AlertTriangle, IndianRupee, Gauge, FileDown } from 'lucide-react';
import ProductionSchedule from './Reports/ProductionSchedule';
import PhoneReports from './Reports/PhoneReports';
import { Table, Stat, money, count } from './Reports/ReportTable';

import { Screen, ScreenSplit, Pane, CommandBar } from '@/components/data';

/**
 * A few choices side by side — the report's own buttons on a laptop, chips a
 * thumb can hit on a phone. Each report keeps its desktop control as the
 * children, so nothing above lg moves.
 */
function Choice({ options, value, onChange, children }) {
  const isMobile = useIsMobile();
  if (!isMobile) return children;
  return (
    <ChipRow
      chips={options.map(([key, label]) => ({ key, label }))}
      active={value}
      onSelect={onChange}
      testIDPrefix="choice"
    />
  );
}

const PERIOD_BUTTON = (on) => `rounded px-2 py-1 text-[11px] font-medium capitalize transition-colors ${
  on ? 'bg-brand-blue/10 text-brand-blue ring-1 ring-inset ring-brand-blue/30' : 'text-slate-500 hover:bg-slate-100'
}`;

function PeriodButtons({ periods, value, onChange }) {
  return (
    <Choice options={periods.map((p) => [p, p])} value={value} onChange={onChange}>
      <div className="flex items-center gap-2">
        <span className="text-[10px] uppercase tracking-wider text-slate-400">Period</span>
        {periods.map(p => (
          <button key={p} type="button" onClick={() => onChange(p)} aria-pressed={value === p} className={PERIOD_BUTTON(value === p)}>
            {p}
          </button>
        ))}
      </div>
    </Choice>
  );
}

/**
 * Status board — where everything is.
 *
 * A job at Die Cutting and a job at Packing are both `in_production`, and that
 * difference is the only thing anyone on the floor wants to know. Process rows
 * come first because they are the answer; lifecycle rows follow as context.
 */
function StatusBoard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get('/reports/status-board').then(({ data }) => setData(data))
      .catch(() => toast.error('Could not load the status board'));
  }, []);

  if (!data) return <p className="py-10 text-center text-sm text-slate-400">Loading…</p>;

  const processRows = data.rows.filter(r => r.kind === 'process');
  const lifecycleRows = data.rows.filter(r => r.kind === 'lifecycle');
  // the server drops the value off the rows for roles that may not see it,
  // rather than sending a number the page is trusted to hide
  const showValue = data.totals.value != null;
  const badgeTone = (row) => (row.kind === 'process' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600');

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Stat label="Orders" value={count(data.totals.orders)} />
        <Stat label="Quantity" value={count(data.totals.quantity)} />
        {showValue && <Stat label="Value" value={money(data.totals.value)} />}
      </div>
      <Table
        rowKey={(row) => row.status}
        sections={[
          { title: 'On the floor', rows: processRows },
          { title: 'Before and after production', rows: lifecycleRows },
        ]}
        empty="Nothing in the system yet."
        columns={[
          { key: 'status', head: 'Where it is', render: (row) => <Badge className={`text-[10px] ${badgeTone(row)}`}>{row.status}</Badge> },
          { key: 'orders', head: 'Orders', align: 'right', render: (row) => count(row.orders) },
          { key: 'quantity', head: 'Quantity', align: 'right', render: (row) => count(row.quantity) },
          { key: 'plan_quantity', head: 'Plan qty', align: 'right', className: 'text-slate-500', render: (row) => count(row.plan_quantity) },
          showValue && { key: 'value', head: 'Value', align: 'right', className: 'font-medium', render: (row) => money(row.value) },
        ]}
      />
    </div>
  );
}

function PendingOrders() {
  const [data, setData] = useState(null);
  const [overdueOnly, setOverdueOnly] = useState(false);

  const load = useCallback(() => {
    api.get('/reports/pending-sales-orders', { params: { overdue_only: overdueOnly, limit: 200 } })
      .then(({ data }) => setData(data))
      .catch(() => toast.error('Could not load pending orders'));
  }, [overdueOnly]);

  useEffect(load, [load]);
  if (!data) return <p className="py-10 text-center text-sm text-slate-400">Loading…</p>;

  const showValue = data.totals.pending_value != null;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Pending qty" value={count(data.totals.pending_qty)} />
        {showValue && <Stat label="Pending value" value={money(data.totals.pending_value)} />}
        {showValue && <Stat label="Outstanding" value={money(data.totals.outstanding)} />}
        <Stat label="Overdue" value={count(data.totals.overdue_orders)}
          tone={data.totals.overdue_orders > 0 ? 'text-red-600' : undefined} />
      </div>
      <Choice
        options={[['all', 'All pending'], ['overdue', 'Overdue only']]}
        value={overdueOnly ? 'overdue' : 'all'}
        onChange={(key) => setOverdueOnly(key === 'overdue')}
      >
        <Button size="sm" variant={overdueOnly ? 'default' : 'outline'} onClick={() => setOverdueOnly(v => !v)}>
          <AlertTriangle className="h-3.5 w-3.5 mr-1" /> {overdueOnly ? 'Showing overdue only' : 'Overdue only'}
        </Button>
      </Choice>
      <Table
        rows={data.rows}
        rowKey={(row) => row.order_id}
        empty="Nothing pending."
        columns={[
          { key: 'order_id', head: 'Order', mono: true, phone: 'sub', render: (row) => row.order_id },
          { key: 'customer', head: 'Customer', phone: 'title', render: (row) => `${row.customer_name}${row.firm_name ? ` · ${row.firm_name}` : ''}` },
          { key: 'location', head: 'Where it is', className: 'text-slate-500', render: (row) => row.location },
          { key: 'due', head: 'Due', render: (row) => row.due_date?.slice(0, 10) || '—' },
          {
            key: 'days', head: 'Days', align: 'right',
            className: (row) => (row.overdue ? 'text-red-600 font-semibold' : ''),
            render: (row) => (row.days_to_due === null ? '—' : row.days_to_due < 0 ? `${Math.abs(row.days_to_due)} late` : row.days_to_due),
          },
          { key: 'ordered', head: 'Ordered', align: 'right', render: (row) => count(row.ordered_qty) },
          { key: 'pending', head: 'Pending', align: 'right', className: 'font-medium', render: (row) => count(row.pending_qty) },
          showValue && { key: 'pending_value', head: 'Pending value', align: 'right', render: (row) => money(row.pending_value) },
          showValue && { key: 'outstanding', head: 'Outstanding', align: 'right', render: (row) => money(row.outstanding) },
        ]}
      />
    </div>
  );
}

function Ageing() {
  const [data, setData] = useState(null);
  const [basis, setBasis] = useState('receivable');

  useEffect(() => {
    api.get('/reports/ageing', { params: { basis } }).then(({ data }) => setData(data))
      .catch(() => toast.error('Could not load the ageing report'));
  }, [basis]);

  if (!data) return <p className="py-10 text-center text-sm text-slate-400">Loading…</p>;

  const bases = [['receivable', 'Money owed'], ['order', 'Orders open']];

  return (
    <div className="space-y-3">
      <Choice options={bases} value={basis} onChange={setBasis}>
        <div className="flex gap-2">
          {bases.map(([value, label]) => (
            <Button key={value} size="sm" variant={basis === value ? 'default' : 'outline'} onClick={() => setBasis(value)}>
              {label}
            </Button>
          ))}
        </div>
      </Choice>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-6">
        {data.buckets.map(b => (
          <Stat key={b.bucket} label={b.bucket} value={money(b.amount)} />
        ))}
      </div>
      <Table
        rows={data.rows}
        rowKey={(row) => row.order_id}
        empty="Nothing outstanding."
        columns={[
          { key: 'order_id', head: 'Order', mono: true, phone: 'sub', render: (row) => row.order_id },
          { key: 'customer', head: 'Customer', phone: 'title', render: (row) => row.customer_name },
          { key: 'order_date', head: 'Order date', render: (row) => row.order_date?.slice(0, 10) },
          { key: 'age', head: 'Age', align: 'right', render: (row) => `${row.age_days}d` },
          { key: 'bucket', head: 'Bucket', render: (row) => <Badge variant="outline" className="text-[10px]">{row.bucket}</Badge> },
          { key: 'value', head: 'Value', align: 'right', render: (row) => money(row.order_value) },
          { key: 'paid', head: 'Paid', align: 'right', className: 'text-slate-500', render: (row) => money(row.paid) },
          { key: 'amount', head: basis === 'receivable' ? 'Owed' : 'Amount', align: 'right', className: 'font-medium', render: (row) => money(row.amount) },
        ]}
      />
    </div>
  );
}

function PoVsDispatch() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get('/reports/po-vs-dispatch').then(({ data }) => setData(data))
      .catch(() => toast.error('Could not load the dispatch comparison'));
  }, []);

  if (!data) return <p className="py-10 text-center text-sm text-slate-400">Loading…</p>;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="On time" value={count(data.summary.on_time)} tone="text-emerald-600" />
        <Stat label="Late" value={count(data.summary.late)} tone={data.summary.late > 0 ? 'text-red-600' : undefined} />
        <Stat label="On-time rate" value={`${data.summary.on_time_pct}%`} />
        <Stat label="Avg days late" value={data.summary.avg_days_late} />
      </div>
      <Table
        rows={data.rows}
        rowKey={(row) => row.order_id}
        empty="Nothing has been dispatched in this window."
        columns={[
          { key: 'order_id', head: 'Order', mono: true, phone: 'sub', render: (row) => row.order_id },
          { key: 'customer', head: 'Customer', phone: 'title', render: (row) => row.customer_name },
          { key: 'po_ref', head: 'PO ref', className: 'text-slate-500', render: (row) => row.po_ref || '—' },
          { key: 'due', head: 'Due', render: (row) => row.due_date?.slice(0, 10) || '—' },
          { key: 'dispatched', head: 'Dispatched', render: (row) => row.dispatched_at?.slice(0, 10) || '—' },
          { key: 'ordered', head: 'Ordered', align: 'right', render: (row) => count(row.ordered_qty) },
          { key: 'dispatched_qty', head: 'Dispatched qty', align: 'right', render: (row) => count(row.dispatched_qty) },
          {
            key: 'variance', head: 'Variance', align: 'right',
            className: (row) => (row.qty_variance < 0 ? 'text-red-600' : ''),
            render: (row) => (row.qty_variance > 0 ? `+${row.qty_variance}` : row.qty_variance),
          },
          {
            key: 'days_late', head: 'Days late', align: 'right',
            className: (row) => (row.on_time ? 'text-emerald-600' : 'text-red-600 font-semibold'),
            render: (row) => (row.days_late === null ? '—' : row.days_late <= 0 ? 'on time' : row.days_late),
          },
        ]}
      />
    </div>
  );
}

/**
 * The rate card as it stands today.
 *
 * The rate master is edited a row at a time and read all at once, so this is
 * the reading view: grouped, dated, with what moved recently marked — nobody
 * should be quoting last month's board rate from memory.
 */
function DailyRateSheet() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get('/reports/daily-rate-sheet').then(({ data }) => setData(data))
      .catch(() => toast.error('Could not load the rate sheet'));
  }, []);

  if (!data) return <p className="py-10 text-center text-sm text-slate-400">Loading…</p>;

  return (
    <div className="space-y-3" data-testid="daily-rate-sheet">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Stat label="Rates on the card" value={count(data.totals.rates)} />
        <Stat label={`Changed in ${data.changed_within_days} days`} value={count(data.totals.recently_changed)}
          tone={data.totals.recently_changed > 0 ? 'text-amber-600' : undefined} />
        <Stat label="As of" value={data.as_of?.slice(0, 10) || '—'} />
      </div>

      {data.groups.length === 0 ? (
        <p className="rounded-lg border border-slate-200 py-10 text-center text-sm text-slate-400">
          No rates on the card yet — add them under Box Costing.
        </p>
      ) : data.groups.map(group => (
        <div key={group.cost_group} className="space-y-1">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            {group.cost_group} <span className="font-normal text-slate-400">· {group.count}</span>
          </p>
          <Table
            rows={group.rates}
            rowKey={(rate) => rate.process}
            empty=""
            columns={[
              {
                key: 'process', head: 'Process',
                render: (rate) => (
                  <>
                    {rate.process}
                    {rate.recently_changed && (
                      <Badge className="ml-1.5 bg-amber-50 text-[9px] text-amber-700">changed</Badge>
                    )}
                  </>
                ),
              },
              { key: 'rate', head: 'Rate', align: 'right', render: (rate) => money(rate.rate) },
              { key: 'per', head: 'Per', align: 'right', render: (rate) => count(rate.per) },
              { key: 'unit', head: 'Unit', className: 'text-slate-500', render: (rate) => rate.unit },
              { key: 'minimum', head: 'Minimum', align: 'right', render: (rate) => (rate.minimum ? money(rate.minimum) : '—') },
              { key: 'remark', head: 'Remark', className: 'text-slate-500', render: (rate) => rate.remark || '—' },
              { key: 'updated', head: 'Updated', className: 'text-slate-500', render: (rate) => rate.updated_at?.slice(0, 10) || '—' },
            ]}
          />
        </div>
      ))}
    </div>
  );
}

/**
 * Revenue, top customers and outstanding — the three that used to live behind a
 * collapsible on the Dashboard, under five tabs of their own. They are reports,
 * and this is the reports page.
 */
function Revenue() {
  const [period, setPeriod] = useState('month');
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get('/analytics/revenue', { params: { period } })
      .then(({ data }) => setData(data))
      .catch(() => toast.error('Could not load revenue'));
  }, [period]);

  return (
    <div className="space-y-3">
      <PeriodButtons periods={['week', 'month', 'quarter', 'year']} value={period} onChange={setPeriod} />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        <Stat label="Billed" value={money(data?.summary?.total_billed)} />
        <Stat label="Collected" value={money(data?.summary?.total_collected)} tone="text-emerald-600" />
        <Stat
          label="Uncollected"
          value={money((data?.summary?.total_billed || 0) - (data?.summary?.total_collected || 0))}
          tone="text-red-600"
        />
      </div>

      <Table
        dense
        rows={data?.buckets || []}
        rowKey={(b) => b.date}
        empty="No revenue in this period"
        columns={[
          { key: 'date', head: 'Period', render: (b) => b.date },
          { key: 'billed', head: 'Billed', className: 'tabular-nums', render: (b) => money(b.billed) },
          { key: 'collected', head: 'Collected', className: 'tabular-nums text-emerald-600', render: (b) => money(b.collected) },
          { key: 'gap', head: 'Gap', className: 'tabular-nums text-red-600', render: (b) => money((b.billed || 0) - (b.collected || 0)) },
        ]}
      />
    </div>
  );
}

/** A customer's name over their firm, the way both money reports list them. */
const customerCell = (c) => (
  <>
    <span className="block font-medium text-slate-800">{c.customer_name}</span>
    {c.firm_name && <span className="block text-[10px] text-slate-400">{c.firm_name}</span>}
  </>
);

function TopCustomers() {
  const [period, setPeriod] = useState('year');
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get('/analytics/top-customers', { params: { period, limit: 25 } })
      .then(({ data }) => setData(data))
      .catch(() => toast.error('Could not load customers'));
  }, [period]);

  const rows = (data?.customers || []).map((c, i) => ({ ...c, rank: i + 1 }));
  const sum = (field) => rows.reduce((s, c) => s + (c[field] || 0), 0);

  return (
    <div className="space-y-3">
      <PeriodButtons periods={['month', 'quarter', 'year']} value={period} onChange={setPeriod} />

      <Table
        dense
        rows={rows}
        rowKey={(c) => c.customer_id || c.rank}
        empty="No customer activity"
        totals={{
          order_count: count(sum('order_count')),
          total_value: money(sum('total_value')),
          total_paid: money(sum('total_paid')),
          outstanding: money(sum('outstanding')),
        }}
        columns={[
          { key: 'rank', head: '#', className: 'text-slate-400', render: (c) => c.rank },
          { key: 'customer', head: 'Customer', phone: 'title', render: customerCell },
          { key: 'order_count', head: 'Orders', className: 'tabular-nums', render: (c) => count(c.order_count) },
          { key: 'total_value', head: 'Billed', className: 'tabular-nums', render: (c) => money(c.total_value) },
          { key: 'total_paid', head: 'Paid', className: 'tabular-nums text-emerald-600', render: (c) => money(c.total_paid) },
          { key: 'outstanding', head: 'Outstanding', className: 'tabular-nums text-red-600', render: (c) => money(c.outstanding) },
        ]}
      />
    </div>
  );
}

function Outstanding() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get('/analytics/outstanding-report')
      .then(({ data }) => setData(data))
      .catch(() => toast.error('Could not load outstanding'));
  }, []);

  const rows = data?.customers || [];

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        <Stat label="Total outstanding" value={money(data?.total_outstanding)} tone="text-red-600" />
        <Stat label="Customers owing" value={count(rows.length)} />
        <Stat
          label="Largest balance"
          value={money(Math.max(0, ...rows.map(c => c.outstanding || 0)))}
          tone="text-red-600"
        />
      </div>

      <Table
        dense
        rows={rows.map((c, i) => ({ ...c, rank: i }))}
        rowKey={(c) => c.rank}
        empty="Nothing outstanding"
        columns={[
          { key: 'customer', head: 'Customer', phone: 'title', render: customerCell },
          { key: 'order_count', head: 'Orders', className: 'tabular-nums', render: (c) => count(c.order_count) },
          { key: 'total_value', head: 'Billed', className: 'tabular-nums', render: (c) => money(c.total_value) },
          { key: 'total_paid', head: 'Paid', className: 'tabular-nums text-emerald-600', render: (c) => money(c.total_paid) },
          { key: 'outstanding', head: 'Outstanding', className: 'tabular-nums font-bold text-red-600', render: (c) => money(c.outstanding) },
        ]}
      />
    </div>
  );
}

/**
 * The report set outgrew a tab bar — thirteen reports will not sit on one row,
 * and the old ERP this office came from lists twenty-seven down the side for
 * exactly that reason. Picking a report is a rail; reading it is the screen.
 */
// `money: true` marks a report the API will refuse for anyone outside
// AGGREGATE_MONEY_ROLES. The floor reports stay for the floor; the value
// columns inside them are dropped server-side, so those render without them.
const REPORTS = [
  { group: 'Where things are', items: [
    { key: 'status', label: 'Status Board', icon: MapPin, render: () => <StatusBoard /> },
    { key: 'pending', label: 'Pending Orders', icon: Clock, render: () => <PendingOrders /> },
    { key: 'schedule', label: 'Machine Load', icon: Gauge, render: () => <ProductionSchedule /> },
  ] },
  { group: 'Late and out', items: [
    { key: 'ageing', label: 'Ageing', icon: AlertTriangle, money: true, render: () => <Ageing /> },
    { key: 'dispatch', label: 'PO vs Dispatch', icon: TruckIcon, render: () => <PoVsDispatch /> },
  ] },
  { group: 'Money', items: [
    { key: 'revenue', label: 'Revenue', icon: BarChart3, money: true, render: () => <Revenue /> },
    { key: 'customers', label: 'Top Customers', icon: BarChart3, money: true, render: () => <TopCustomers /> },
    { key: 'outstanding', label: 'Outstanding', icon: IndianRupee, money: true, render: () => <Outstanding /> },
    { key: 'rates', label: 'Rate Sheet', icon: IndianRupee, money: true, render: () => <DailyRateSheet /> },
  ] },
];

export default function ReportsPage() {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const showMoney = canSeeAggregateMoney(user);

  const groups = REPORTS
    .map(g => ({ ...g, items: g.items.filter(r => showMoney || !r.money) }))
    .filter(g => g.items.length);
  const allReports = groups.flatMap(g => g.items);

  const [active, setActive] = useState('status');
  const report = allReports.find(r => r.key === active) || allReports[0];

  const exportCsv = async (kind) => {
    try {
      await downloadAuthedFile(`/analytics/export/${kind}`, `${kind}.csv`);
    } catch {
      toast.error('Export failed');
    }
  };

  if (isMobile) {
    return (
      <PhoneReports reports={allReports} active={report.key} onSelect={setActive} showMoney={showMoney} onExport={exportCsv}>
        {report.render()}
      </PhoneReports>
    );
  }

  return (
    <Screen data-testid="reports-page">
      <CommandBar
        title="Reports"
        subtitle={report?.label}
        actions={showMoney ? [
          { key: 'export-orders', label: 'Export orders', icon: FileDown, onClick: () => exportCsv('orders') },
          { key: 'export-outstanding', label: 'Export outstanding', icon: FileDown, onClick: () => exportCsv('outstanding') },
        ] : []}
      />

      <ScreenSplit
        railWidth="14rem"
        rail={
          <Pane title="Reports" aside={`${allReports.length}`} className="max-h-full">
            {/* A vertical tab list: one report is shown at a time, and the rail
                is how you choose which. Grouped, because thirteen reports in one
                flat column is the wall the tab bar was already becoming. */}
            <div role="tablist" aria-orientation="vertical" aria-label="Reports">
              {groups.map(group => (
                <div key={group.group}>
                  <p className="border-b border-slate-100 bg-slate-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                    {group.group}
                  </p>
                  {group.items.map(item => {
                    const Icon = item.icon;
                    const on = item.key === active;
                    return (
                      <button
                        key={item.key}
                        type="button"
                        role="tab"
                        id={`report-tab-${item.key}`}
                        aria-selected={on}
                        aria-controls="report-panel"
                        tabIndex={on ? 0 : -1}
                        onClick={() => setActive(item.key)}
                        className={`flex w-full items-center gap-2 border-b border-slate-100 px-2.5 py-1.5 text-left text-[13px] transition-colors last:border-b-0 ${
                          on ? 'bg-brand-blue/10 font-semibold text-brand-blue' : 'text-slate-600 hover:bg-slate-50'
                        }`}
                      >
                        <Icon className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate">{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </Pane>
        }
      >
        <Pane title={report.label} bodyClassName="p-3">
          <div role="tabpanel" id="report-panel" aria-labelledby={`report-tab-${report.key}`}>
            {report.render()}
          </div>
        </Pane>
      </ScreenSplit>
    </Screen>
  );
}
